import type { Locale } from '../i18n'

export type LegalDoc = {
  title: string
  updated: string
  intro: string
  sections: { heading: string; body: string[] }[]
}

const UPDATED = '2026-10-12'

/** 規約は日本語と英語だけを正本として持つ。ほかの言語には英語版を出す */
type LegalLocale = 'ja' | 'en'

const terms: Record<LegalLocale, LegalDoc> = {
  ja: {
    title: '利用規約',
    updated: UPDATED,
    intro:
      'この利用規約（以下「本規約」）は、Focus Cafe（以下「本サービス」）の利用条件を定めるものです。本サービスを利用した時点で、本規約に同意したものとみなします。',
    sections: [
      {
        heading: '第1条（本サービスの内容）',
        body: [
          '本サービスは、タイマーによる集中の記録と、同じ時間に集中している人の在室状況の表示、作業ログとシェア用画像の作成を提供するオンラインのコワーキングスペースです。',
          '本サービスの内容は、予告なく変更、追加または終了することがあります。',
        ],
      },
      {
        heading: '第2条（アカウント）',
        body: [
          '利用者は、Google アカウントでのログイン、またはゲストとして本サービスを利用できます。',
          'ゲストの記録は利用中のブラウザに紐づきます。ブラウザのデータを削除すると、ゲストの記録にはアクセスできなくなります。Google アカウントと連携すると、ゲストの記録を引き継げます。',
          '利用者は、自己の責任でアカウントを管理するものとします。',
        ],
      },
      {
        heading: '第3条（公開される情報）',
        body: [
          'Google アカウントで利用する場合、表示名、プロフィール画像、作業ログ（日時、集中時間、作業メモ）、累計時間などの統計は、プロフィールページと作業ログのページで誰でも閲覧できる状態で公開されます。',
          '在室中は、表示名とプロフィール画像が他の利用者に表示されます。ゲストの表示名は「ゲスト」と表示されます。',
          '作業メモには、他人に見られて困る情報を書かないでください。',
        ],
      },
      {
        heading: '第4条（禁止事項）',
        body: [
          '利用者は、次の行為をしてはなりません。法令または公序良俗に反する行為。他の利用者または第三者の権利を侵害する行為。作業メモへの差別的、攻撃的、わいせつな内容、個人情報、広告の投稿。本サービスのサーバーやネットワークに過度な負荷をかける行為。不正な手段で記録を作成する行為。本サービスの運営を妨げる行為。',
        ],
      },
      {
        heading: '第5条（投稿内容の扱い）',
        body: [
          '作業メモの著作権は利用者に帰属します。利用者は、本サービスの提供と紹介に必要な範囲で、作業メモとシェア用画像を無償で利用（表示、複製、送信）することを運営者に許諾します。',
          '運営者は、本規約に反すると判断した投稿を、予告なく非表示または削除できます。',
        ],
      },
      {
        heading: '第6条（利用の停止）',
        body: [
          '運営者は、利用者が本規約に違反した場合、事前の通知なく本サービスの利用を停止し、またはアカウントを削除できます。',
        ],
      },
      {
        heading: '第7条（免責）',
        body: [
          '本サービスは現状のまま提供されます。運営者は、本サービスが中断なく動作すること、記録が失われないこと、利用者の目的に適合することを保証しません。',
          '本サービスの利用によって利用者に生じた損害について、運営者は故意または重過失がある場合を除き責任を負いません。',
        ],
      },
      {
        heading: '第8条（規約の変更）',
        body: [
          '運営者は、必要に応じて本規約を変更できます。変更後の規約は、本サービス上に掲示した時点から効力を持ちます。',
        ],
      },
      {
        heading: '第9条（準拠法と管轄）',
        body: [
          '本規約は日本法に準拠します。本サービスに関して紛争が生じた場合は、東京地方裁判所を第一審の専属的合意管轄裁判所とします。',
        ],
      },
    ],
  },
  en: {
    title: 'Terms of Service',
    updated: UPDATED,
    intro:
      'These Terms of Service ("Terms") govern your use of Focus Cafe (the "Service"). By using the Service you agree to these Terms.',
    sections: [
      {
        heading: '1. The Service',
        body: [
          'Focus Cafe is an online co-working space. It records focus sessions with a timer, shows who else is focusing right now, and creates focus logs and shareable images.',
          'We may change, add to or discontinue the Service at any time without notice.',
        ],
      },
      {
        heading: '2. Accounts',
        body: [
          'You can use the Service by signing in with Google or as a guest.',
          'Guest records are tied to the browser you use. If you clear your browser data you will lose access to them. Linking a Google account keeps your guest records.',
          'You are responsible for managing your account.',
        ],
      },
      {
        heading: '3. Public information',
        body: [
          'If you use a Google account, your display name, profile picture, focus logs (date, duration and note) and statistics such as total focus time are public on your profile page and log pages.',
          'While you are in the café, your display name and picture are shown to other people. Guests are shown as "Guest".',
          'Do not write anything in a note that you would not want others to see.',
        ],
      },
      {
        heading: '4. Prohibited conduct',
        body: [
          'You must not: break the law; infringe the rights of others; post discriminatory, abusive or obscene content, personal information or advertising in notes; place excessive load on our servers or network; create records by illegitimate means; or interfere with the operation of the Service.',
        ],
      },
      {
        heading: '5. Your content',
        body: [
          'You keep the copyright in your notes. You grant us a free licence to display, copy and transmit your notes and share images as needed to operate and promote the Service.',
          'We may hide or delete content that we believe violates these Terms without notice.',
        ],
      },
      {
        heading: '6. Suspension',
        body: [
          'If you violate these Terms we may suspend your use of the Service or delete your account without prior notice.',
        ],
      },
      {
        heading: '7. Disclaimer',
        body: [
          'The Service is provided as is. We do not guarantee that it will run without interruption, that records will never be lost, or that it fits your purpose.',
          'Except in cases of intent or gross negligence, we are not liable for damages arising from your use of the Service.',
        ],
      },
      {
        heading: '8. Changes',
        body: ['We may update these Terms. Updated Terms take effect when posted on the Service.'],
      },
      {
        heading: '9. Governing law',
        body: [
          'These Terms are governed by the laws of Japan. The Tokyo District Court has exclusive jurisdiction as the court of first instance.',
        ],
      },
    ],
  },
}

const privacy: Record<LegalLocale, LegalDoc> = {
  ja: {
    title: 'プライバシーポリシー',
    updated: UPDATED,
    intro: 'Focus Cafe（以下「本サービス」）は、利用者の情報を次のとおり取り扱います。',
    sections: [
      {
        heading: '1. 取得する情報',
        body: [
          'Google アカウントでログインした場合: 表示名、メールアドレス、プロフィール画像、Google のアカウント識別子。',
          '本サービスの利用に伴う情報: 集中の開始・終了時刻、集中時間、作業メモ、シェア用画像、タイムゾーン、表示言語。',
          'ログインの維持に必要な情報: セッション Cookie、IP アドレス、ブラウザの種類（User-Agent）。',
        ],
      },
      {
        heading: '2. 利用目的',
        body: [
          '本サービスの提供（在室状況の表示、作業ログ、プロフィール、シェア用画像の作成）、不正利用の防止、障害の調査、サービスの改善のために利用します。',
          'メールアドレスは公開せず、ログインとアカウントの識別にのみ使います。',
        ],
      },
      {
        heading: '3. 公開される情報',
        body: [
          'Google アカウントで利用する場合、表示名、プロフィール画像、作業ログ（日時、集中時間、作業メモ）、統計はプロフィールページと作業ログのページで公開されます。ゲストの記録は公開されません。',
        ],
      },
      {
        heading: '4. 外部サービス',
        body: [
          '本サービスは Cloudflare, Inc. のサービス（Workers、D1、Workers KV、Durable Objects）上で動作し、データは同社の設備に保存されます。',
          'ログインには Google LLC の認証を使います。フォントの表示に Google Fonts を使います。',
        ],
      },
      {
        heading: '5. Cookie',
        body: [
          'ログイン状態の維持のために Cookie を使います。広告やトラッキングを目的とした Cookie は使いません。',
        ],
      },
      {
        heading: '6. 第三者提供',
        body: ['法令に基づく場合を除き、本人の同意なく個人情報を第三者に提供しません。'],
      },
      {
        heading: '7. 開示・訂正・削除',
        body: [
          '利用者は、自身の情報の開示、訂正、削除を求めることができます。アカウントを削除すると、作業ログとシェア用画像も削除します。',
        ],
      },
      {
        heading: '8. 改定',
        body: [
          '本ポリシーは必要に応じて改定します。改定後の内容は本サービス上に掲示した時点から効力を持ちます。',
        ],
      },
    ],
  },
  en: {
    title: 'Privacy Policy',
    updated: UPDATED,
    intro: 'This policy explains how Focus Cafe (the "Service") handles your information.',
    sections: [
      {
        heading: '1. Information we collect',
        body: [
          'When you sign in with Google: your display name, email address, profile picture and Google account identifier.',
          'When you use the Service: start and end times of focus sessions, focus duration, notes, share images, time zone and language.',
          'To keep you signed in: a session cookie, your IP address and browser type (User-Agent).',
        ],
      },
      {
        heading: '2. How we use it',
        body: [
          'To provide the Service (showing who is in the café, focus logs, profiles and share images), to prevent abuse, to investigate problems and to improve the Service.',
          'Your email address is never made public. We use it only to sign you in and identify your account.',
        ],
      },
      {
        heading: '3. What is public',
        body: [
          'If you use a Google account, your display name, profile picture, focus logs (date, duration and note) and statistics are public on your profile and log pages. Guest records are not public.',
        ],
      },
      {
        heading: '4. Third-party services',
        body: [
          'The Service runs on Cloudflare, Inc. (Workers, D1, Workers KV and Durable Objects), and your data is stored in their infrastructure.',
          'Sign-in uses Google LLC authentication. Fonts are served by Google Fonts.',
        ],
      },
      {
        heading: '5. Cookies',
        body: ['We use cookies to keep you signed in. We do not use cookies for advertising or tracking.'],
      },
      {
        heading: '6. Sharing',
        body: [
          'We do not share personal information with third parties without your consent, except where required by law.',
        ],
      },
      {
        heading: '7. Access and deletion',
        body: [
          'You can ask us to disclose, correct or delete your information. Deleting your account also deletes your focus logs and share images.',
        ],
      },
      {
        heading: '8. Changes',
        body: ['We may update this policy. Updates take effect when posted on the Service.'],
      },
    ],
  },
}

const legal = { terms, privacy }

export function legalDoc(kind: 'terms' | 'privacy', locale: Locale): { doc: LegalDoc; lang: LegalLocale } {
  const lang: LegalLocale = locale === 'ja' ? 'ja' : 'en'
  return { doc: legal[kind][lang], lang }
}
